import { useState } from 'react'
import { FiAlertCircle, FiX, FiCheck, FiXCircle } from 'react-icons/fi'
import toast from 'react-hot-toast'
import { API_URL } from '../../config/api'
import { Btn } from '../../ui'
import { formatearFecha } from '../../utils/fechas'

/*
 * ModalCorreccionesPropuestas (V2) — popup de inicio con DS v2.
 * Muestra propuestas de corrección del admin esperando respuesta del empleado.
 */

const ModalCorreccionesPropuestasV2 = ({ propuestas, onClose, onRespondida }) => {
  const [loading, setLoading] = useState(null)
  const [rechazoId, setRechazoId] = useState(null)
  const [comentarioRechazo, setComentarioRechazo] = useState('')

  const handleResponder = async (id, accion, comentario = '') => {
    try {
      setLoading(id)
      const token = localStorage.getItem('token')
      const response = await fetch(`${API_URL}/correcciones/${id}/responder`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ accion, comentario })
      })
      const data = await response.json()
      if (!response.ok) throw new Error(data.error || 'Error al procesar')
      toast.success(accion === 'aprobar' ? 'Corrección aceptada' : 'Corrección rechazada')
      if (onRespondida) onRespondida()
    } catch (error) {
      toast.error(error.message || 'Error al procesar la propuesta')
    } finally {
      setLoading(null)
      setRechazoId(null)
      setComentarioRechazo('')
    }
  }

  const handleRechazar = (id) => {
    if (rechazoId === id) {
      if (!comentarioRechazo.trim()) { toast.error('Indica el motivo del rechazo'); return }
      handleResponder(id, 'rechazar', comentarioRechazo)
    } else {
      setRechazoId(id)
      setComentarioRechazo('')
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="ui-card w-full max-w-2xl max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-start gap-3 p-5 border-b border-[var(--line)]">
          <div className="p-2 rounded-soft bg-[rgba(245,158,11,.10)]">
            <FiAlertCircle size={20} className="text-[#A86A06]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="eyebrow mb-1">Pendiente de respuesta</div>
            <h2 className="font-display text-[24px] text-[var(--ink)] leading-tight">
              {propuestas.length} {propuestas.length === 1 ? 'corrección propuesta' : 'correcciones propuestas'}.
            </h2>
            <p className="text-[12.5px] text-[var(--ink-3)] mt-1">
              Tu administrador ha propuesto cambios en tus fichajes. Revisa y acepta o rechaza.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-soft text-[var(--ink-3)] hover:text-[var(--ink)] hover:bg-[var(--bg-2)] transition-colors"
            aria-label="Cerrar"
          >
            <FiX size={16} />
          </button>
        </div>

        {/* Lista */}
        <div className="overflow-y-auto flex-1 p-5 space-y-3">
          {propuestas.map(propuesta => (
            <div
              key={propuesta.id}
              className="rounded-card border border-[rgba(245,158,11,.25)] bg-[rgba(245,158,11,.04)] p-4"
            >
              <div className="mb-3">
                <p className="font-medium text-[var(--ink)] capitalize">
                  {formatearFecha(propuesta.fecha)}
                </p>
                {propuesta.solicitante_nombre && (
                  <p className="text-[11px] text-[var(--ink-3)] mt-0.5">
                    Propuesto por: {propuesta.solicitante_nombre}
                  </p>
                )}
              </div>

              {/* Horarios */}
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div className="rounded-soft p-2.5 bg-[var(--bg)] border border-[var(--line)]">
                  <p className="text-[10px] text-[var(--ink-3)] font-mono uppercase tracking-wider mb-1">Horario original</p>
                  <div className="flex items-center gap-1.5 text-[13px] font-mono tnum">
                    <span>{propuesta.hora_entrada_original || '-'}</span>
                    <span className="text-[var(--ink-3)]">→</span>
                    <span>{propuesta.hora_salida_original || '-'}</span>
                  </div>
                </div>
                <div className="rounded-soft p-2.5 bg-[rgba(16,185,129,.06)] border border-[rgba(16,185,129,.20)]">
                  <p className="text-[10px] text-[#0F8C66] font-mono uppercase tracking-wider mb-1">Horario propuesto</p>
                  <div className="flex items-center gap-1.5 text-[13px] font-mono tnum font-medium text-[#0F8C66]">
                    <span>{propuesta.hora_entrada_corregida || '-'}</span>
                    <span>→</span>
                    <span>{propuesta.hora_salida_corregida || '-'}</span>
                  </div>
                </div>
              </div>

              {/* Motivo */}
              <div className="rounded-soft p-2.5 bg-[var(--bg)] border border-[var(--line)] mb-3">
                <p className="text-[10px] text-[var(--ink-3)] font-mono uppercase tracking-wider mb-1">Motivo</p>
                <p className="text-[13px] text-[var(--ink-2)]">{propuesta.motivo}</p>
              </div>

              {/* Textarea para rechazo */}
              {rechazoId === propuesta.id && (
                <div className="mb-3">
                  <textarea
                    value={comentarioRechazo}
                    onChange={(e) => setComentarioRechazo(e.target.value)}
                    placeholder="Indica el motivo del rechazo…"
                    className="w-full px-3 py-2 bg-[var(--bg)] border border-[var(--line)] rounded-soft text-[13px] resize-none focus:outline-none focus:border-[#C13030]"
                    rows={2}
                    autoFocus
                  />
                </div>
              )}

              {/* Botones */}
              <div className="flex gap-2">
                <Btn
                  variant="primary"
                  size="sm"
                  className="flex-1"
                  onClick={() => handleResponder(propuesta.id, 'aprobar')}
                  disabled={loading === propuesta.id}
                >
                  <FiCheck size={13} /> Aceptar
                </Btn>
                <Btn
                  variant="ghost"
                  size="sm"
                  className="flex-1"
                  onClick={() => handleRechazar(propuesta.id)}
                  disabled={loading === propuesta.id}
                >
                  <FiXCircle size={13} />
                  {rechazoId === propuesta.id ? 'Confirmar rechazo' : 'Rechazar'}
                </Btn>
                {rechazoId === propuesta.id && (
                  <Btn variant="soft" size="sm" onClick={() => { setRechazoId(null); setComentarioRechazo('') }}>
                    Cancelar
                  </Btn>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-[var(--line)]">
          <Btn variant="ghost" className="w-full justify-center" onClick={onClose}>
            Recordar más tarde
          </Btn>
        </div>
      </div>
    </div>
  )
}

export default ModalCorreccionesPropuestasV2
